import {
  converObjectErrorInString,
  convertObjectToCamelCase,
  convertObjectToSnakeCase,
} from '@/helpers'
import { useMutation } from '@tanstack/react-query'

import {
  BaseAddressProps,
  CalculateLogisticCostsPayloadProps,
  CalculateLogisticCostsResponseProps,
  CalculateLogisticCustomsCostsPayloadProps,
  CalculateMarginPayloadProps,
  CalculateMarginProps,
  CalculateUnitPricePayloadProps,
  CalculateUnitPriceProps,
  GetAddressPayloadProps,
} from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'
import {
  ADDRESS_URL,
  BFF_CALCULATE_LOGISTIC_CUSTOMS_COSTS_URL,
  CALCULATE_LOGISTIC_COSTS_URL,
  CALCULATE_MARGIN_URL,
  CALCULATE_UNIT_PRICE_URL
} from './api.urls'

export const useCalculateMargin = () => {
  const calculateMargin = async (
    payload: CalculateMarginPayloadProps,
  ): Promise<{
    success: boolean
    message: string
    data?: CalculateMarginProps
  }> => {
    try {
      const { data } = await api.post(
        CALCULATE_MARGIN_URL,
        convertObjectToSnakeCase(
          !payload?.deliveryOption
            ? { ...payload, deliveryOption: 'receive_at_address' }
            : payload,
        ),
      )

      return {
        success: true,
        message: '',
        data: convertObjectToCamelCase(data) as CalculateMarginProps,
      }
    } catch (err) {
      return {
        success: false,
        message: '',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.CALCULATE_MARGIN.module],
    mutationFn: (payload: CalculateMarginPayloadProps) =>
      calculateMargin(payload),
  })
}

export const useCalculateUnitPrice = () => {
  const calculateUnitPrice = async (
    payload: CalculateUnitPricePayloadProps,
  ): Promise<{
    success: boolean
    message: string
    data?: CalculateUnitPriceProps
  }> => {
    try {
      const { data } = await api.post(
        CALCULATE_UNIT_PRICE_URL,
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: '',
        data: convertObjectToCamelCase(data) as CalculateUnitPriceProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: err?.response?.data,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.CALCULATE_UNIT_PRICE_URL.module],
    mutationFn: (payload: CalculateUnitPricePayloadProps) =>
      calculateUnitPrice(payload),
  })
}

export const useCalculateLogisticCosts = () => {
  const calculateLogisticCosts = async (
    payload: CalculateLogisticCostsPayloadProps,
  ): Promise<{
    success: boolean
    message: string
    data?: CalculateLogisticCostsResponseProps
  }> => {
    try {
      const { data } = await api.post(
        CALCULATE_LOGISTIC_COSTS_URL,
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: '',
        data: convertObjectToCamelCase(data) as CalculateLogisticCostsResponseProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: err?.response?.data,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.CALCULATE_LOGISTIC_COSTS_URL.module],
    mutationFn: (payload: CalculateLogisticCostsPayloadProps) =>
      calculateLogisticCosts(payload),
  })
}

export const useGetAddress = () => {
  const getAddress = async (
    payload: GetAddressPayloadProps,
  ): Promise<{
    success: boolean
    message: string
    data?: BaseAddressProps[]
  }> => {
    try {
      const { data } = await api.get(
        `${ADDRESS_URL}${payload.country}/${payload.postalCode}/`,
      )

      return {
        success: true,
        message: '',
        data: convertObjectToCamelCase(data) as BaseAddressProps[],
      }
    } catch (err) {
      return {
        success: false,
        message: '',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ADDRESS.module],
    mutationFn: (payload: GetAddressPayloadProps) => getAddress(payload),
  })
}

export const useCalculateLogisticCustomsCosts = () => {
  const calculateLogisticCustomsCosts = async (
    payload: CalculateLogisticCustomsCostsPayloadProps,
  ): Promise<{
    success: boolean
    message: string
    data?: {
      customsCost?: number
    }
  }> => {
    try {
      const { data } = await api.post(
        BFF_CALCULATE_LOGISTIC_CUSTOMS_COSTS_URL,
        { ...payload, vehicleCapacity: payload?.vehicleCapacity || 20000, currency: payload?.currency || 'USD' },
      )

      return {
        success: true,
        message: '',
        data: convertObjectToCamelCase(data) as {
          customsCost?: number
        },
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: err?.response?.data,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_CALCULATE_LOGISTIC_CUSTOMS_COSTS_URL.module],
    mutationFn: (payload: CalculateLogisticCustomsCostsPayloadProps) =>
      calculateLogisticCustomsCosts(payload),
  })
}